import { Injectable } from '@angular/core';
import { generateClient } from '@aws-amplify/api';
import * as queries from '@app/core/graphql/queries';
import {
    CreateRfqInput,
    DeleteRfqInput,
    ModelSortDirection,
    ModelRfqFilterInput,
    Rfq,
    UpdateRfqInput
} from '@app/core/services/api/API.service';
import * as mutations from '@graphql/mutations';

@Injectable({
    providedIn: 'root'
})
export class RfqService {
    private amplifyClient = generateClient();

    constructor() {}

    async listAllRfqsByClient(
        estatus: string,
        clientId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        pageLimit: number = 25
    ): Promise<Rfq[]> {
        const rfqs: Rfq[] = [];
        let nextToken: string | null = null;
        const filter: ModelRfqFilterInput = {
            clientId: { eq: clientId }
        };

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.listRfqsByClient,
                        variables: {
                            estatus,
                            filter,
                            sortDirection,
                            limit: pageLimit,
                            nextToken
                        }
                    })
                ).data.listRfqsByClient;

                if (response?.items) {
                    rfqs.push(...(response.items as Rfq[]));
                }
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);

            return rfqs;
        } catch (error) {
            console.error('Error listing RFQs by client:', error);
            return [];
        }
    }

    async listAllRfqsByCreated(
        estatus: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        pageLimit: number = 25
    ): Promise<Rfq[]> {
        const rfqs: Rfq[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.listRfqsByCreated,
                        variables: {
                            estatus,
                            sortDirection,
                            limit: pageLimit,
                            nextToken
                        }
                    })
                ).data.listRfqsByCreated;

                if (response?.items) {
                    rfqs.push(...(response.items as Rfq[]));
                }
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);

            return rfqs;
        } catch (error) {
            console.error('Error listing RFQs by created:', error);
            return [];
        }
    }

    async listRfqs(limit: number = 1000): Promise<Rfq[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listRfqs,
                    variables: { limit }
                })
            ).data.listRfqs.items;
            return (data ?? []) as Rfq[];
        } catch (error) {
            console.error('Error listing RFQs:', error);
            return [];
        }
    }

    async getRfq(id: string): Promise<Rfq> {
        try {
            const rfq = (
                await this.amplifyClient.graphql({
                    query: queries.getRfq,
                    variables: {
                        id
                    }
                })
            ).data.getRfq;
            return rfq as Rfq;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async updateRfq(body: UpdateRfqInput): Promise<Rfq> {
        try {
            const rfq = (
                await this.amplifyClient.graphql({
                    query: mutations.updateRfq,
                    variables: {
                        input: body
                    }
                })
            ).data.updateRfq;
            return rfq as Rfq;
        } catch (err) {
            console.log(err);
        }
    }

    async createRfq(input: CreateRfqInput): Promise<Rfq> {
        try {
            const rfq = (
                await this.amplifyClient.graphql({
                    query: mutations.createRfq,
                    variables: {
                        input
                    }
                })
            ).data.createRfq;
            if (rfq) return rfq as Rfq;
        } catch (error) {
            console.error('Error creating RFQ:', error);
            return null;
        }
    }

    async deleteRfq(input: DeleteRfqInput): Promise<Rfq> {
        try {
            const deleteRfq = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteRfq,
                    variables: {
                        input
                    }
                })
            ).data.deleteRfq;
            return deleteRfq as Rfq;
        } catch {
            console.error('Error deleted RFQ:', Error);
            return null;
        }
    }
}
